import { SalesOrderCreatedEvent } from '@rematter/frontend-lib-api-client/grpc/trade/salesOrder_events_pb';
import { SalesOrderStatus } from '@rematter/frontend-lib-api-client/grpc/trade/salesOrder_pb';
import {
  BadgeField,
  Col,
  FieldDisplay,
  humanReadableDateTime,
  IDField,
  ThemeColor,
  TinyTable,
} from '@rematter/ui-web';

import {
  salesOrderTypeLabels,
  salesOrderStatusLabels,
  unitLabels,
} from '../enumLabels';

import { protoTimestampToZonedDateTimeString } from '@/audit-log/_components/_shared';
import { getLocale } from '~/shared/utils/locale';

const getStatusColor = (status: SalesOrderStatus): ThemeColor => {
  switch (status) {
    case SalesOrderStatus.DRAFT:
      return 'neutral';
    case SalesOrderStatus.OPEN:
      return 'primary';
    case SalesOrderStatus.FINISHED:
      return 'success';
    case SalesOrderStatus.VOIDED:
      return 'failure';
    default:
      return 'neutral';
  }
};

type SalesOrderCreatedDetailProps = {
  event: SalesOrderCreatedEvent;
};

export const SalesOrderCreatedDetail = ({
  event,
}: SalesOrderCreatedDetailProps) => {
  const salesOrder = event.salesOrder;

  if (!salesOrder) {
    return (
      <Col className="@SalesOrderCreatedDetail items-center justify-center p-4 text-neutral/40">
        <span>No data available</span>
      </Col>
    );
  }

  const startDateStr = protoTimestampToZonedDateTimeString(
    salesOrder.startDate,
  );
  const endDateStr = protoTimestampToZonedDateTimeString(salesOrder.endDate);

  return (
    <Col className="@SalesOrderCreatedDetail gap-3" gap3>
      <Col className="rounded-md border border-neutral/10 bg-white p-3" gap2>
        <span className="text-xs font-medium text-neutral/60">
          Sales Order Details
        </span>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          <IDField label="ID" value={salesOrder.id?.value} />
          <FieldDisplay
            label="Document Number"
            value={salesOrder.documentNumber}
          />
          <BadgeField
            label="Type"
            value={salesOrderTypeLabels[salesOrder.type]}
            color="teal"
          />
          <BadgeField
            label="Status"
            value={salesOrderStatusLabels[salesOrder.status]}
            color={getStatusColor(salesOrder.status)}
          />
          <IDField label="Facility ID" value={salesOrder.facilityId?.value} />
          <IDField label="Account ID" value={salesOrder.accountId?.value} />
          <IDField label="Contact ID" value={salesOrder.contactId?.value} />
          <FieldDisplay
            label="Counterparty Reference"
            value={salesOrder.counterpartyReferenceNumber}
          />
          <FieldDisplay
            label="Payment Currency"
            value={salesOrder.paymentCurrency}
          />
          {startDateStr && (
            <FieldDisplay
              label="Start Date"
              value={humanReadableDateTime(startDateStr, {
                locale: getLocale(),
              })}
            />
          )}
          {endDateStr && (
            <FieldDisplay
              label="End Date"
              value={humanReadableDateTime(endDateStr, { locale: getLocale() })}
            />
          )}
        </div>
      </Col>

      {salesOrder.lineItems && salesOrder.lineItems.length > 0 && (
        <Col className="rounded-md border border-neutral/10 bg-white p-3" gap1>
          <span className="mb-2 text-xs font-medium text-neutral/60">
            Line Items ({salesOrder.lineItems.length})
          </span>
          <TinyTable
            rowKey={(item) => item.id?.value ?? String(Math.random())}
            columns={[
              {
                key: 'itemId',
                label: 'Item ID',
                cell: (item) => (
                  <span className="font-mono text-xs">
                    {item.itemId?.value ?? '-'}
                  </span>
                ),
              },
              {
                key: 'quantity',
                label: 'Quantity',
                width: 100,
                cell: (item) => item.quantity ?? '-',
              },
              {
                key: 'unit',
                label: 'Unit',
                width: 120,
                cell: (item) => unitLabels[item.unit] ?? '-',
              },
            ]}
            data={salesOrder.lineItems}
            isLoading={false}
          />
        </Col>
      )}
    </Col>
  );
};
